import { logEvent, Analytics } from "firebase/analytics";
import { analytics } from "../firebase/config";

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogEventParams {
  [key: string]: string | number | boolean | undefined;
}

export interface LogOptions {
  level?: LogLevel;
  userId?: string;
  userProperties?: Record<string, string | number | boolean>;
  params?: LogEventParams;
}

export class LoggerService {
  private analytics: Analytics;
  private isDevelopment: boolean;

  constructor() {
    this.analytics = analytics;
    this.isDevelopment = process.env.REACT_APP_ENV === "development";
  }

  /**
   * Log an event to Firebase Analytics and optionally to console
   */
  async logEvent(eventName: string, params?: LogEventParams, options?: LogOptions): Promise<void> {
    const logLevel = options?.level || "info";

    try {
      // Always log to console in development
      if (this.isDevelopment) {
        this.logToConsole(eventName, params, logLevel, options);
      }

      // Prepare analytics parameters
      const analyticsParams: LogEventParams = {
        ...params,
        timestamp: Date.now(),
        environment: this.isDevelopment ? "development" : "production",
      };

      // Add user properties if provided
      if (options?.userId) {
        analyticsParams.user_id = options.userId;
      }

      // Log to Firebase Analytics
      await logEvent(this.analytics, eventName, analyticsParams);

      if (this.isDevelopment) {
        console.log("📊 Event logged to Firebase Analytics:", eventName, analyticsParams);
      }
    } catch (error) {
      // Fallback to console logging if Firebase Analytics fails
      console.error("❌ Failed to log event to Firebase Analytics:", error);
      if (!this.isDevelopment) {
        // In production, still log to console if analytics fails
        this.logToConsole(eventName, params, "error", options);
      }
    }
  }

  /**
   * Log debug messages (only in development)
   */
  debug(message: string, params?: LogEventParams, options?: Omit<LogOptions, "level">): Promise<void> {
    return this.logEvent(`debug_${this.sanitizeEventName(message)}`, params, {
      ...options,
      level: "debug",
    });
  }

  /**
   * Log info messages
   */
  info(message: string, params?: LogEventParams, options?: Omit<LogOptions, "level">): Promise<void> {
    return this.logEvent(`info_${this.sanitizeEventName(message)}`, params, {
      ...options,
      level: "info",
    });
  }

  /**
   * Log warning messages
   */
  warn(message: string, params?: LogEventParams, options?: Omit<LogOptions, "level">): Promise<void> {
    return this.logEvent(`warning_${this.sanitizeEventName(message)}`, params, {
      ...options,
      level: "warn",
    });
  }

  /**
   * Log error messages
   */
  error(message: string, error?: Error, params?: LogEventParams, options?: Omit<LogOptions, "level">): Promise<void> {
    const errorParams: LogEventParams = {
      ...params,
      error_message: error?.message,
      error_stack: error?.stack,
      error_name: error?.name,
    };

    return this.logEvent(`error_${this.sanitizeEventName(message)}`, errorParams, {
      ...options,
      level: "error",
    });
  }

  /**
   * Log user actions/interactions
   */
  userAction(action: string, params?: LogEventParams, options?: Omit<LogOptions, "level">): Promise<void> {
    return this.logEvent(`user_${this.sanitizeEventName(action)}`, params, {
      ...options,
      level: "info",
    });
  }

  /**
   * Log page/screen views
   */
  pageView(pageName: string, params?: LogEventParams, options?: Omit<LogOptions, "level">): Promise<void> {
    return this.logEvent("page_view", {
      page_title: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...params,
    }, {
      ...options,
      level: "info",
    });
  }

  /**
   * Log feature usage
   */
  featureUsed(featureName: string, params?: LogEventParams, options?: Omit<LogOptions, "level">): Promise<void> {
    return this.logEvent("feature_used", {
      feature_name: featureName,
      ...params,
    }, {
      ...options,
      level: "info",
    });
  }

  /**
   * Log performance metrics
   */
  performance(metricName: string, value: number, unit?: string, params?: LogEventParams): Promise<void> {
    return this.logEvent("performance_metric", {
      metric_name: metricName,
      metric_value: value,
      metric_unit: unit || "ms",
      ...params,
    });
  }

  /**
   * Log business/conversion events
   */
  conversion(eventName: string, value?: number, currency?: string, params?: LogEventParams): Promise<void> {
    return this.logEvent("conversion", {
      conversion_event: eventName,
      value: value,
      currency: currency || "USD",
      ...params,
    });
  }

  /**
   * Private method to log to console with proper formatting
   */
  private logToConsole(eventName: string, params?: LogEventParams, level: LogLevel = "info", options?: LogOptions): void {
    const timestamp = new Date().toISOString();
    const logData = {
      timestamp,
      event: eventName,
      level,
      params,
      userId: options?.userId,
      userProperties: options?.userProperties,
    };

    const logMethod = this.getConsoleMethod(level);
    const emoji = this.getLevelEmoji(level);

    logMethod(`${emoji} [${timestamp}] ${eventName.toUpperCase()}`, logData);
  }

  /**
   * Get appropriate console method for log level
   */
  private getConsoleMethod(level: LogLevel): (...args: unknown[]) => void {
    switch (level) {
      case "debug":
        return console.debug;
      case "info":
        return console.info;
      case "warn":
        return console.warn;
      case "error":
        return console.error;
      default:
        return console.log;
    }
  }

  /**
   * Get emoji for log level
   */
  private getLevelEmoji(level: LogLevel): string {
    switch (level) {
      case "debug":
        return "🐛";
      case "info":
        return "ℹ️";
      case "warn":
        return "⚠️";
      case "error":
        return "❌";
      default:
        return "📝";
    }
  }

  /**
   * Sanitize event name for Firebase Analytics
   */
  private sanitizeEventName(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-zA-Z0-9_]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "")
      .substring(0, 40); // Firebase Analytics event name limit
  }
}

// Create and export singleton instance
export const logger = new LoggerService();

export default LoggerService;
